import { gql } from '@apollo/client';
import {
  RiskPredictionResultTypeFragment,
  RiskPredictionVariableContributionModelTypeFragment,
} from '../backoffice_risk/fragments';
import {
  CollectionActionsFields,
} from '../collection/fragments';
import {
  CompanyCessionsHistoryFields,
  CompanyDebtsSummary,
  CompanyDocumentsFields,
  CompanyReceiverCessionsFields,
  MasterEntityBasicField,
  ReceiverCessionsHistoryFields,
  ReceiverDebtsSummary,
  RiskBlacklistFields,
  MasterEntityExternalRiskStatusFields,
} from '../customers/fragment';
import { PreofferEvaluationInfo } from '../evaluation_requests/fragments';
import {
  InvoiceBasicFields,
  InvoiceCollectionFields,
  InvoiceComplianceFields,
  InvoiceFilesFields,
  InvoiceMoneyTransferFields,
  InvoicePaymentDiscountFields,
  InvoiceRatesFields,
  InvoiceRatificationFields,
  InvoiceReintegrationFields,
  InvoiceReplacementFields,
  InvoiceTransferFields,
  OrderingAssignedFields,
  TypeConnectionFields,
  sharedInvoiceInput,
  sharedInvoiceInputFields,
  BankAccountFields,
} from '../fragments';
import CompanyEvaluationRestrictionMinimalFields from '../fragments/company-evaluation-restriction/company-evaluation-restriction-minimal-fields';
import MoneyFields from '../fragments/money/money-fields';
import CommissionRuleFields from '../fragments/commission-rule/commission-rule-fields';
import { RatificationActionsBasicFields } from '../ratification/fragments';
import { UserIdentification } from '../user/fragments';
import {
  CessionsFields,
  CollectionManagerFields,
  CreditNoteFields,
  DocumentFinanceStateFields,
  DownloadInvoiceDocumentFields,
  InvoiceAttachableFields,
  InvoiceDocumentsFields,
  InvoiceDteTypeFields,
  InvoiceHistoryStatusFields,
  InvoiceIdFields,
  InvoiceIntermediateReferenceFields,
  InvoiceReferenceFields,
  InvoiceRequestingPlatformFields,
  InvoiceStatusFields,
  OfferFields,
  PreofferFields,
  TraceFields,
} from './fragments';
import { InvoiceReviewDocumentsFields } from '../fragments/invoice/invoice-review-documents-fields';

export const SIMPLE_INVOICE = gql`
  query GetSimpleInvoice($invoiceId: ID!) {
    invoice(invoiceId: $invoiceId) {
      ...InvoiceIdFields
      company {
        id
        masterEntity {
          ...MasterEntityBasicField
        }
      }
      receiver {
        ...MasterEntityBasicField
      }
    }
  }
  ${InvoiceIdFields}
  ${MasterEntityBasicField}
`;

export const GET_INVOICE = gql`
  query GetInvoice($invoiceId: ID!) {
    invoice(invoiceId: $invoiceId) {
      ...InvoiceBasicFields
      ...InvoiceFilesFields
      ...InvoiceCollectionFields
      ...InvoiceRatificationFields
      ...InvoiceRatesFields
    }
  }
  ${InvoiceBasicFields}
  ${InvoiceFilesFields}
  ${InvoiceCollectionFields}
  ${InvoiceRatificationFields}
  ${InvoiceRatesFields}
`;

export const GET_INVOICE_PROFILE = gql`
  query GetInvoice($invoiceId: ID!) {
    invoice(invoiceId: $invoiceId) {
      ...InvoiceBasicFields
      ...InvoiceFilesFields
      ...InvoiceCollectionFields
      ...InvoiceRatificationFields
      ...InvoiceReplacementFields
      ...InvoiceReintegrationFields
      ...InvoiceRatesFields
      ...OrderingAssignedFields
      ...InvoiceIntermediateReferenceFields
      ...DownloadInvoiceDocumentFields
    }
  }
  ${InvoiceBasicFields}
  ${InvoiceFilesFields}
  ${InvoiceCollectionFields}
  ${InvoiceRatificationFields}
  ${InvoiceReplacementFields}
  ${InvoiceReintegrationFields}
  ${InvoiceIntermediateReferenceFields}
  ${InvoiceRatesFields}
  ${OrderingAssignedFields}
  ${DownloadInvoiceDocumentFields}
`;

export const MY_DOCUMENTS = gql`
  query myDocuments(
    ${sharedInvoiceInputFields}
  ) {
    invoices(
      ${sharedInvoiceInput}
    ) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          ... InvoiceBasicFields
        }
      }
    }
  }
  ${TypeConnectionFields}
  ${InvoiceBasicFields}
`;

export const INVOICE_CREATE_OPERATION = gql`
  query invoiceCreateOperation(${sharedInvoiceInputFields}) {
    invoices(${sharedInvoiceInput}) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          ... InvoiceIdFields
          ... InvoiceReviewDocumentsFields
          offer {
            ... OfferFields
          }
        }
      }
    }
  }
  ${TypeConnectionFields}
  ${InvoiceIdFields}
  ${InvoiceReviewDocumentsFields}
  ${OfferFields}
`;

export const AVAILABLE_FACTORING = gql`
  query availableFactoring(${sharedInvoiceInputFields}) {
    invoices(${sharedInvoiceInput}) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          ... InvoiceIdFields
          ... InvoiceStatusFields
          ... InvoiceReferenceFields
          preofferSelectable @client
          isForOrdering
          hasPaymentsEmitted
          invoiceStatus @client
          amounts @client
          lightningPaymentState @client
          receiver {
            ... MasterEntityBasicField
          }
          amountWithIva {
            ... MoneyFields
          }
          company {
            id
            masterEntity {
              ... MasterEntityBasicField
            }
            executiveAssigned {
              ... UserIdentification
            }
          }
          dteType {
            ... InvoiceDteTypeFields
          }
          preoffer {
            ... PreofferFields
          }
          documents {
            ... InvoiceDocumentsFields
          }
          uniqueDocumentFinanceState {
            ... DocumentFinanceStateFields
          }
          cessions {
            ... CessionsFields
          }
        }
      }
    }
  }
  ${TypeConnectionFields}
  ${InvoiceIdFields}
  ${InvoiceStatusFields}
  ${InvoiceDteTypeFields}
  ${InvoiceDocumentsFields}
  ${PreofferFields}
  ${MasterEntityBasicField}
  ${InvoiceReferenceFields}
  ${DocumentFinanceStateFields}
  ${CessionsFields}
  ${UserIdentification}
  ${MoneyFields}
`;

export const CLIENT_ASSIGNMENTS = gql`
  query clientAssignments(${sharedInvoiceInputFields}) {
    invoices(${sharedInvoiceInput}) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          ... InvoiceIdFields
          ... InvoiceStatusFields
          ... InvoiceReferenceFields
          ... InvoicePaymentDiscountFields
          ... InvoiceReviewDocumentsFields
          receiver {
            ... MasterEntityBasicField
          }
          collectionManager {
            ... CollectionManagerFields
          }
          offer {
            ... OfferFields
            transferCondition
          }
          cessions {
            ... CessionsFields
          }
          historystatusSet {
            ...InvoiceHistoryStatusFields
          }
          lastHistoryStatus{
            ...InvoiceHistoryStatusFields
          }
        }
      }
    }
  }
  ${TypeConnectionFields}
  ${InvoiceIdFields}
  ${InvoiceStatusFields}
  ${MasterEntityBasicField}
  ${CollectionManagerFields}
  ${InvoiceReferenceFields}
  ${InvoiceReviewDocumentsFields}
  ${OfferFields}
  ${CessionsFields}
  ${InvoiceHistoryStatusFields}
  ${InvoicePaymentDiscountFields}
`;

export const INVOICE_ATTACHABLE_DOCUMENTS = gql`
  query InvoiceAttachableDocuments($invoiceId: ID!) {
    invoice(invoiceId: $invoiceId) {
      id
      folio
      ...InvoiceAttachableFields
      company {
        id
        name
        rut
        documents {
          ...CompanyDocumentsFields
        }
      }
    }
  }
  ${InvoiceAttachableFields}
  ${CompanyDocumentsFields}
`;

export const INVOICE_REQUIREMENTS_DOCUMENTS = gql`
  query invoiceRequirementDocuments($invoiceId: ID!) {
    invoice(invoiceId: $invoiceId) {
      id
      ...InvoiceAttachableFields
    }
  }
  ${InvoiceAttachableFields}
`;
export const MY_OFFERS = gql`
  query myOffers(
    ${sharedInvoiceInputFields}
  ) {
    invoices(
      ${sharedInvoiceInput}
    ) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          ... InvoiceBasicFields
          ... InvoiceRatesFields
          receiver {
            invoicesActualPaymentTerm
          }
          restrictedBy {
            ...CompanyEvaluationRestrictionMinimalFields
          }
          documents {
            ... InvoiceDocumentsFields
          }
        }
      }
    }
  }
  ${TypeConnectionFields}
  ${InvoiceBasicFields}
  ${InvoiceRatesFields}
  ${CompanyEvaluationRestrictionMinimalFields}
  ${InvoiceDocumentsFields}
`;

export const PENDING_SIGNATURE = gql`
  query pendingSignature(
    ${sharedInvoiceInputFields}
  ) {
    invoices(
      ${sharedInvoiceInput}
    ) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          ... InvoiceIdFields
          ... InvoiceStatusFields
          invoiceStatus @client
          company {
            id
            rut
            name
            executiveAssigned {
              ... UserIdentification
            }
            masterEntity {
              ... MasterEntityBasicField
            }
          }
          receiver {
            ... MasterEntityBasicField
          }
          pendingsignaturecontractannexinvoice {
            id
            pendingSignatureDocument {
              id
              globalAppId
              createdAt
              lastFile
              paybackFile
            }
          }
          operation {
            id
            bankAccount {
              ... BankAccountFields
            }
          }
        }
      }
    }
  }
  ${TypeConnectionFields}
  ${InvoiceIdFields}
  ${InvoiceStatusFields}
  ${MasterEntityBasicField}
  ${UserIdentification}
  ${BankAccountFields}
`;

export const INVOICE_TRANSFER_INFO = gql`
  query getInvoiceTreasuryFields(
    ${sharedInvoiceInputFields}
  ) {
    invoices(
      ${sharedInvoiceInput}
    ) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          ... InvoiceComplianceFields
          ... InvoiceTransferFields
          ... InvoicePaymentDiscountFields
          ... InvoiceMoneyTransferFields
          ... InvoiceRatesFields
          ... InvoiceRequestingPlatformFields
          intermediateReferencesFrom {
            toInvoice {
            ... InvoiceIdFields
            dteType {
              ... InvoiceDteTypeFields
              }
            }
            amount {
              ... MoneyFields
            }
          }
          creditNotes @client {
            ... InvoiceIdFields
            dteType {
              ... InvoiceDteTypeFields
            }
          }
          debitNotes @client {
            ... InvoiceIdFields
            dteType {
              ... InvoiceDteTypeFields
            }
          }
          isFirstOperation
          isForOrdering
          hasPaymentsEmitted
          collectionManager {
            id
          }
          company {
            hasActiveContract
            masterEntity {
              ...CompanyDebtsSummary
            }
          }
          receiver {
            ...ReceiverDebtsSummary
            displayNationalIdentifier
          }
          paymentDays
          operationDays
          ... InvoiceReviewDocumentsFields
          offer {
            id
          }
        }
      }
    }
  }
  ${CompanyDebtsSummary}
  ${ReceiverDebtsSummary}
  ${TypeConnectionFields}
  ${InvoiceComplianceFields}
  ${InvoiceTransferFields}
  ${InvoiceIdFields}
  ${MoneyFields}
  ${InvoiceDteTypeFields}
  ${InvoiceMoneyTransferFields}
  ${InvoicePaymentDiscountFields}
  ${InvoiceRatesFields}
  ${InvoiceRequestingPlatformFields}
  ${InvoiceReviewDocumentsFields}
`;

export const INVOICE_AMOUNT_DETAILS = gql`
  query getInvoiceAmountsDetails(
    ${sharedInvoiceInputFields}
  ) {
    invoices(
      ${sharedInvoiceInput}
    ) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          ... InvoiceBasicFields
          ... InvoicePaymentDiscountFields
          ... InvoiceCollectionFields
        }
      }
    }
  }
  ${InvoiceCollectionFields}
  ${TypeConnectionFields}
  ${InvoiceBasicFields}
  ${InvoicePaymentDiscountFields}
`;

export const RECOMMENDED_INVOICES = gql`
  query recommendedInvoices(
    ${sharedInvoiceInputFields}
  ) {
    invoices(
      ${sharedInvoiceInput}
    ) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          ... InvoiceBasicFields
          ... InvoiceRatesFields
        }
      }
    }
  }
  ${TypeConnectionFields}
  ${InvoiceBasicFields}
  ${InvoiceRatesFields}
`;

export const GET_CLIENT_DEBTORS_LIST_EXCEL = gql`
  query getClientDebtorsListExcel($clientRut: String!) {
    getClientDebtorsListExcel(clientRut: $clientRut) {
      url
    }
  }
`;

export const INVOICES_RATIFICATION = gql`
  query invoicesRatification(
    ${sharedInvoiceInputFields}
  ) {
    invoices(
      ${sharedInvoiceInput}
    ) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          ... InvoiceIdFields
          ... InvoiceStatusFields
          ... CreditNoteFields
          ... InvoiceRequestingPlatformFields
          lightningPaymentState @client
          operationalRoute
          company {
            id
            executiveAssigned {
              ... UserIdentification
            }
            masterEntity {
              ... MasterEntityBasicField
              clientExternalRiskStatus {
                ...MasterEntityExternalRiskStatusFields
              }
              riskBlacklist {
                ...RiskBlacklistFields
              }
              ...CompanyDebtsSummary
            }
          }
          receiver {
            ... MasterEntityBasicField
            debtorExternalRiskStatus {
              ...MasterEntityExternalRiskStatusFields
            }
            riskBlacklist {
              ...RiskBlacklistFields
            }
            ...ReceiverDebtsSummary
            debtor {
              id
              ratifies
              ratificationInformation {
                id
                comment
              }
            }
          }
          dteType {
            ... InvoiceDteTypeFields
          }
          traces {
            ... TraceFields
          }
          uniqueDocumentFinanceState {
            ... DocumentFinanceStateFields
          }
          preoffer {
            id
            preofferevaluationrequest {
              id
              paymentDate
            }
          }
          amountUsedInEntityLine {
            ... MoneyFields
          }
          offer {
            transferCondition
          }
        }
      }
    }
  }
  ${CompanyDebtsSummary}
  ${ReceiverDebtsSummary}
  ${TypeConnectionFields}
  ${InvoiceIdFields}
  ${InvoiceStatusFields}
  ${InvoiceDteTypeFields}
  ${MasterEntityBasicField}
  ${UserIdentification}
  ${TraceFields}
  ${DocumentFinanceStateFields}
  ${CreditNoteFields}
  ${MoneyFields}
  ${RiskBlacklistFields}
  ${InvoiceRequestingPlatformFields}
  ${MasterEntityExternalRiskStatusFields}
`;

export const GET_RISK_VARIABLES = gql`
  query getRiskVariables($objectId: Int!, $modelName: String!) {
    getRiskVariables(objectId: $objectId, modelName: $modelName) {
      fetchVariables {
        id
        name
        value
        valueType
        variableType
        labelName
        source
        description
      }
    }
  }
`;

export const GET_RISK_PREDICTION_RESULTS = gql`
  query getRiskPredictionResults($riskEvaluationId: Int!) {
    getRiskPredictionResults(riskEvaluationId: $riskEvaluationId) {
      ...RiskPredictionResultTypeFragment
      predictionModel {
        id
        releasedDatetime
        predictionType {
          id
          resultEnum
        }
      }
      variablesContribution {
        ...RiskPredictionVariableContributionModelTypeFragment
      }
    }
  }
  ${RiskPredictionResultTypeFragment}
  ${RiskPredictionVariableContributionModelTypeFragment}
`;

export const RERUN_EVALUATION_REQUEST = gql`
  mutation rerunEvaluationRequest(
    $objectId: Int!
    $objectType: String!
    $clientDicom: Boolean!
    $debtorDicom: Boolean!
    $clientBusinessProfile: Boolean!
    $debtorBusinessProfile: Boolean!
    $clientCurrentBehavior: Boolean!
    $debtorCurrentBehavior: Boolean!
  ) {
    rerunEvaluationRequest(
      objectId: $objectId
      objectType: $objectType
      clientDicom: $clientDicom
      debtorDicom: $debtorDicom
      clientBusinessProfile: $clientBusinessProfile
      debtorBusinessProfile: $debtorBusinessProfile
      clientCurrentBehavior: $clientCurrentBehavior
      debtorCurrentBehavior: $debtorCurrentBehavior
    ) {
      success
    }
  }
`;

export const GET_AUTOMATIC_RESPONSE_EMAIL_PREVIEW = gql`
  query getAutomaticResponseEmailPreview(
    $documentIds: [String]!
    $modelName: String!
  ) {
    getAutomaticResponseEmailPreview(
      documentIds: $documentIds
      modelName: $modelName
    ) {
      preview
    }
  }
`;

export const GENERATE_RISK_PDF_LINK = gql`
  query generateRiskPdfLink($invoiceIds: [Int]!) {
    generateRiskPdfLink(invoiceIds: $invoiceIds)
  }
`;

export const PREEVALUATION_EMAIL_SEND = gql`
  mutation GenerateAutomaticPreOfferResponseEmail(
    $documentsIds: [String]!
    $modelName: String!
    $acceptPreoffer: Boolean!
    $finalDescription: String
    $emailSending: String!
    $isPrivate: Boolean!
  ) {
    generateAutomaticPreOfferResponseEmail(
      documentsIds: $documentsIds
      modelName: $modelName
      acceptPreoffer: $acceptPreoffer
      finalDescription: $finalDescription
      emailSending: $emailSending
      isPrivate: $isPrivate
    ) {
      success
    }
  }
`;

export const REJECT_ORDERING_SIMULATION = gql`
  mutation rejectOrderingSimulation(
    $requestResolutions: [PurchaseOrderEvaluationResolutionRejectType]
    $sendEmail: Boolean!
    $analystDescription: String
  ) {
    rejectOrderingSimulation(
      requestResolutions: $requestResolutions
      sendEmail: $sendEmail
      analystDescription: $analystDescription
    ) {
      success
    }
  }
`;

export const APPROVE_ORDERING_SIMULATION = gql`
  mutation approveOrderingSimulation(
    $requestResolutions: [SimulationApprovedType]!
    $sendEmail: Boolean!
    $analystDescription: String
  ) {
    approveOrderingSimulation(
      requestResolutions: $requestResolutions
      sendEmail: $sendEmail
      analystDescription: $analystDescription
    ) {
      success
    }
  }
`;

export const DOCUMENTS_TYPES = gql`
  query documentsTypes {
    documentsTypes {
      id
      code
      name
      shortName
      reviewDocumentManagerConstrain
    }
  }
`;

export const INVOICES_HISTORY_EVALUATIONS = gql`
  query invoice(
    $preoffer_Preofferevaluationrequest_Status_In: [String]
    $orderBy: String
    $first: Int
    $companyId: Int
    $receiverId: Int
  ) {
    invoices(
      preoffer_Preofferevaluationrequest_Status_In: $preoffer_Preofferevaluationrequest_Status_In
      orderBy: $orderBy
      first: $first
      companyId: $companyId
      receiverId: $receiverId
    ) {
      totalCount
      totalPages
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          ...InvoiceIdFields
          dteType {
            ...InvoiceDteTypeFields
          }
          company {
            id
            masterEntity {
              ...MasterEntityBasicField
            }
            cessionsHistory {
              id
              cessionsNumber
              cessionsMoneyAmount
              historicCessionsNumber
              historicCessionsMoneyAmount
            }
          }
          receiver {
            ...MasterEntityBasicField
            receiverCessionsHistory {
              id
              cessionsNumber
              cessionsMoneyAmount
              historicCessionsNumber
              historicCessionsMoneyAmount
            }
          }
          companyReceiverCessionHistory {
            id
            cessionsNumber
            cessionsMoneyAmount
            historicCessionsNumber
            historicCessionsMoneyAmount
          }
          preoffer {
            id
            preofferevaluationrequest {
              ...PreofferEvaluationInfo
              user {
                ...UserIdentification
              }
              assignedEvaluator {
                ...UserIdentification
              }
            }
          }
        }
      }
    }
  }
  ${InvoiceIdFields}
  ${InvoiceDteTypeFields}
  ${UserIdentification}
  ${PreofferEvaluationInfo}
  ${MasterEntityBasicField}
`;

export const CONFIRMINGS_QUERY = gql`
  query invoices(
    ${sharedInvoiceInputFields}
  ) {
    invoices(
      ${sharedInvoiceInput}
    ) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          id
          relatedConfirming {
            id
            folio
            dateToPay
            createdAt
            amountWithIva {
              ... MoneyFields
            }
            advanceAmount {
              ... MoneyFields
            }
            company {
              id
              masterEntity {
                ... MasterEntityBasicField
                riskBlacklist {
                  ...RiskBlacklistFields
                }
              }
              companyevaluationrestrictionsSet {
                id
                status
                restriction
                description
                resolutionExplanation
              }
              documents {
                ... CompanyDocumentsFields
              }
            }
            receiver {
              id
              masterEntity {
                ... MasterEntityBasicField
                riskBlacklist {
                  ...RiskBlacklistFields
                }
              }
            }
            invoiceMoneyTransfer {
              id
              createdAt
            }
          }
        }
      }
    }
  }
  ${MoneyFields}
  ${TypeConnectionFields}
  ${MasterEntityBasicField}
  ${CompanyDocumentsFields}
  ${RiskBlacklistFields}
`;

export const MONTHLY_RATE_INVOICE = gql`
  query monthlyRateInvoice(
    $clientIdentifier: String
    $debtorIdentifier: String
    $utmCampaign: String!
    $encryptedMailLogId: String!
  ) {
    monthlyRateInvoice(
      clientIdentifier: $clientIdentifier
      debtorIdentifier: $debtorIdentifier
      utmCampaign: $utmCampaign
      encryptedMailLogId: $encryptedMailLogId
    ) {
      monthlyRate
      defaultRate
      retentionRate
      segment {
        id
        rules {
          ...CommissionRuleFields
        }
      }
    }
  }
  ${CommissionRuleFields}
`;

export const GET_INVOICES_FOR_CESSION_CHANGE_MAIL = gql`
  query GetInvoicesForCessionChangeMail(
    ${sharedInvoiceInputFields}
  ) {
    invoices(
      ${sharedInvoiceInput}
    ) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          id
          status
          company {
            masterEntity {
              id
            }
          }
          documents {
            type {
              code
            }
          }
        }
      }
    }
  }
  ${TypeConnectionFields}
`;

export const INVOICE_CESSIONS_TOOLTIP = gql`
  query invoiceCessionsTooltip($invoiceId: ID!) {
    invoice(invoiceId: $invoiceId) {
      id
      company {
        id
        cessionsHistory {
          ...CompanyCessionsHistoryFields
        }
      }
      receiver {
        id
        receiverCessionsHistory {
          ...ReceiverCessionsHistoryFields
        }
      }
      companyReceiverCessionHistory {
        ...CompanyReceiverCessionsFields
      }
    }
  }
  ${CompanyCessionsHistoryFields}
  ${ReceiverCessionsHistoryFields}
  ${CompanyReceiverCessionsFields}
`;

export const INVOICE_OVERFLOW_TOOLTIP = gql`
  query invoiceOverflowTooltip($invoiceId: ID!) {
    invoice(invoiceId: $invoiceId) {
      id
      overflow {
        rejectionType
        reason
      }
    }
  }
`;

export const CLIENT_FACTORING_INFORMATION = gql`
  query clientFactoringInformation($companyId: Int, $receiverId: Int) {
    exposedInvoices: invoices(
      companyId: $companyId
      receiverId: $receiverId
      status: ["Debt", "Transfered", "PartialCollection"]
      operation_Isnull: false
      first: 1
    ) {
      totalCount
      aggregate(field: "amount_with_iva")
      edges {
        cursor
        node {
          id
        }
      }
    }
    debtInvoices: invoices(
      companyId: $companyId
      receiverId: $receiverId
      status: ["Debt"]
      operation_Isnull: false
      first: 1
    ) {
      totalCount
      aggregate(field: "amount_with_iva")
      edges {
        cursor
        node {
          id
        }
      }
    }
    finishedInvoices: invoices(
      companyId: $companyId
      receiverId: $receiverId
      finishedByPayment: true
      status: ["Finished"]
      operation_Isnull: false
      first: 10
    ) {
      totalCount
      aggregate(field: "amount_with_iva")
      edges {
        cursor
        node {
          id
        }
      }
    }
  }
`;

export const CLIENT_INVOICES_STATUS_INFORMATION = gql`
  query clientInvoicesStatusInformation(
    $companyId: Int
    $receiverId: Int
    $dateIssued_Gte: Date
  ) {
    amountRejectedInvoices: invoices(
      companyId: $companyId
      receiverId: $receiverId
      dateIssued_Gte: $dateIssued_Gte
      siiStatus: ["Rejected"]
      first: 1
    ) {
      totalCount
      edges {
        cursor
        node {
          id
        }
      }
    }
    amountCreditNoteInvoices: invoices(
      companyId: $companyId
      receiverId: $receiverId
      dateIssued_Gte: $dateIssued_Gte
      dteType_Code: "61"
      first: 1
    ) {
      totalCount
      edges {
        cursor
        node {
          id
        }
      }
    }
    amountOfPendingReintegrations: invoices(
      companyId: $companyId
      receiverId: $receiverId
      availableForReintegration: true
      first: 1
    ) {
      totalCount
      edges {
        cursor
        node {
          id
        }
      }
    }
  }
`;

export const FACTORING_PROFILE_INVOICES = gql`
  query factoringProfileInvoices(
    ${sharedInvoiceInputFields}
  ) {
    invoices(
      ${sharedInvoiceInput}
    ) {
      totalCount
      totalPages
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          ... InvoiceIdFields
          ... InvoiceStatusFields
          company {
            id
            masterEntity {
              ... MasterEntityBasicField
            }
          }
          receiver {
            ... MasterEntityBasicField
          }
          dteType {
            ... InvoiceDteTypeFields
          }
          historystatusSet {
            ... InvoiceHistoryStatusFields
          }
          uniqueDocumentFinanceState {
            ... DocumentFinanceStateFields
          }
          collectionManager {
            id
            forReintegration
            actions {
              ... CollectionActionsFields
              author {
                ... UserIdentification
              }
            }
          }
          ratificationmanager {
            id
            actions {
              ... RatificationActionsBasicFields
              author {
                ... UserIdentification
              }
            }
          }
          amountUsedInEntityLine {
            ... MoneyFields
          }
        }
      }
    }
  }
  ${InvoiceIdFields}
  ${InvoiceStatusFields}
  ${InvoiceDteTypeFields}
  ${MasterEntityBasicField}
  ${InvoiceHistoryStatusFields}
  ${DocumentFinanceStateFields}
  ${CollectionActionsFields}
  ${RatificationActionsBasicFields}
  ${UserIdentification}
  ${MoneyFields}
`;

export const GET_INVOICE_QUANTITIES = gql`
query getInvoiceQuantities(
  ${sharedInvoiceInputFields}
) {
  invoices(
    ${sharedInvoiceInput}
  ) {
    totalCount
  }
}
`;

export const INVOICES_RELATED_COMPANY_ANNEX = gql`
  query GetSimpleInvoice($invoiceId: ID!) {
    invoice(invoiceId: $invoiceId) {
      id
      pendingsignaturecontractannexinvoice {
        id
        pendingSignatureDocument {
          id
          invoices {
            edges {
              cursor
              node {
                id
                folio
                dateIssued
                receiver {
                  ...MasterEntityBasicField
                }
                amountWithIva {
                  ...MoneyFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${MasterEntityBasicField}
  ${MoneyFields}
`;

export const INVOICE_FOLIOS = gql`
  query invoiceFiles(
    ${sharedInvoiceInputFields}
  ) {
    invoices(
      ${sharedInvoiceInput}
    ) {
      ... TypeConnectionFields
      edges {
        cursor
        node {
          id
          folio
        }
      }

    }
  }
  ${TypeConnectionFields}
`;
